import axios, { AxiosRequestConfig } from 'axios';

const LOCAL_API = 'http://localhost:8000';
const DEFAULT_BACKEND_API = 'https://tkanopolis.ru';
export const API_BASE_LAST_OK_KEY = 'api_base_last_ok';

const stripTrailingSlash = (url: string) => url.replace(/\/$/, '');

const STOREFRONT_HOSTS = new Set([
  'tkanopolis.ru',
  'www.tkanopolis.ru',
  'tekstil-pro.site',
  'www.tekstil-pro.site',
  'runatex.site',
  'www.runatex.site',
  'runo.site',
  'www.runo.site',
]);

const isStorefrontHost = (host: string): boolean => STOREFRONT_HOSTS.has(host);

const currentStorefrontOrigin = (): string | null => {
  if (typeof window === 'undefined') return null;
  if (!isStorefrontHost(window.location.hostname)) return null;
  return stripTrailingSlash(window.location.origin);
};


function resolveConfiguredApiBase(): string | null {
  const raw = process.env.REACT_APP_API_URL?.trim();
  if (!raw) return null;

  const lower = raw.toLowerCase();
  if (lower === 'same-origin' || lower === 'same_origin' || raw === '/') {
    if (typeof window !== 'undefined') {
      return stripTrailingSlash(window.location.origin);
    }
    return null;
  }

  return stripTrailingSlash(raw);
}

const envLocksApiBase = (): boolean => !!resolveConfiguredApiBase();

function isTrustedApiBase(base: string): boolean {
  try {
    const u = new URL(base);
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
      return true;
    }
    if (isStorefrontHost(u.hostname)) {
      return !u.port || u.port === '443' || u.port === '80';
    }
    return u.port === '8000' && u.hostname.includes('tkaniruna');
  } catch {
    return false;
  }
}

export const clearApiBaseCache = (): void => {
  localStorage.removeItem(API_BASE_LAST_OK_KEY);
};

const purgeInvalidApiBaseCache = (): void => {
  if (envLocksApiBase()) {
    clearApiBaseCache();
    return;
  }
  const lastOk = localStorage.getItem(API_BASE_LAST_OK_KEY);
  if (lastOk && !isTrustedApiBase(lastOk)) {
    clearApiBaseCache();
  }
};

purgeInvalidApiBaseCache();

function resolveApiBaseUrl(): string {
  const storefront = currentStorefrontOrigin();
  if (storefront) {
    return storefront;
  }

  const fromEnv = resolveConfiguredApiBase();
  if (fromEnv) {
    return fromEnv;
  }

  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return LOCAL_API;
  }

  if (host.includes('tkaniruna')) {
    return `${window.location.protocol}//${host}:8000`;
  }

  return DEFAULT_BACKEND_API;
}

export const API_BASE_URL = resolveApiBaseUrl();


export const getApiBaseCandidates = (): string[] => {
  const storefront = currentStorefrontOrigin();
  if (storefront) {
    return [storefront];
  }

  const fromEnv = resolveConfiguredApiBase();
  if (fromEnv) {
    return [fromEnv];
  }

  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return [LOCAL_API];
  }

  if (host.includes('tkaniruna')) {
    return [`${window.location.protocol}//${host}:8000`];
  }

  const lastOk = localStorage.getItem(API_BASE_LAST_OK_KEY);
  if (lastOk && isTrustedApiBase(lastOk)) {
    return [lastOk];
  }

  return [DEFAULT_BACKEND_API];
};

const applyWorkingBase = (base: string) => {
  const normalized = base.replace(/\/$/, '');
  if (!envLocksApiBase()) {
    localStorage.setItem(API_BASE_LAST_OK_KEY, normalized);
  }
  axios.defaults.baseURL = normalized;
  return normalized;
};

const WARMUP_COOLDOWN_MS = 60_000;
let warmupInFlight: Promise<boolean> | null = null;
let lastWarmupSuccessAt = 0;

export const warmupApi = async (): Promise<boolean> => {
  const now = Date.now();
  if (
    lastWarmupSuccessAt > 0 &&
    now - lastWarmupSuccessAt < WARMUP_COOLDOWN_MS &&
    (envLocksApiBase() || localStorage.getItem(API_BASE_LAST_OK_KEY))
  ) {
    return true;
  }
  if (warmupInFlight) {
    return warmupInFlight;
  }

  warmupInFlight = (async () => {
    const base = getApiBaseCandidates()[0];
    try {
      await axios.get(`${base}/api/v1/health`, { timeout: 15000 });
      applyWorkingBase(base);
      lastWarmupSuccessAt = Date.now();
      return true;
    } catch {
      if (!envLocksApiBase()) clearApiBaseCache();
      return false;
    }
  })();

  try {
    return await warmupInFlight;
  } finally {
    warmupInFlight = null;
  }
};

const isApiResponseError = (err: any): boolean => !!err?.response?.status;

const requestWithBase = async <T,>(
  method: 'get' | 'post',
  path: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> => {
  const base = getApiBaseCandidates()[0];
  const timeout = config?.timeout ?? (method === 'post' ? 30000 : 25000);

  if (method === 'get') {
    const res = await axios.get<T>(`${base}${path}`, { ...config, timeout });
    applyWorkingBase(base);
    return res.data;
  }

  const res = await axios.post<T>(`${base}${path}`, data, { ...config, timeout });
  applyWorkingBase(base);
  return res.data;
};

export const getWithFallback = async <T,>(
  path: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    return await requestWithBase<T>('get', path, undefined, config);
  } catch (err: any) {
    if (isApiResponseError(err)) throw err;
    throw err;
  }
};

export const postWithFallback = async <T,>(
  path: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    return await requestWithBase<T>('post', path, data, config);
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 401 || status === 403 || isApiResponseError(err)) {
      throw err;
    }
    throw err;
  }
};

export const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
};

axios.defaults.baseURL = API_CONFIG.baseURL;
axios.defaults.timeout = API_CONFIG.timeout;
axios.defaults.headers.common['Content-Type'] = 'application/json';

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = String(error.config?.url || '');
    const isLoginAttempt = requestUrl.includes('/login-json');

    if (error.response?.status === 401 && !isLoginAttempt) {
      localStorage.removeItem('token');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userName');

      if (!window.location.hash.includes('/login') && !window.location.pathname.includes('/login')) {
        alert('Сессия истекла. Пожалуйста, войдите снова.');
        window.location.href = '/#/';
      }
    }
    return Promise.reject(error);
  }
);
